import React, { useCallback, useMemo, useState, memo } from 'react';
import { setRecall } from 'store/ducks/leftPanel/recall';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from 'hooks';
import * as S from './styles';
import { useDispatch } from 'react-redux';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { api, notify } from 'services';

const PWA_DASHBOARD_URL =
  process.env.REACT_APP_YMS_PORTARIA_CLIENT_URL ??
  'https://app.agendamento.webcol.systems/app/dashboard';
const PWA_BASE_URL = PWA_DASHBOARD_URL.replace(
  /\/app\/dashboard\/?$/,
  ''
).replace(/\/$/, '');
const PWA_ARRIVALS_URL = `${PWA_BASE_URL}/app/arrivals`;
const PWA_DOCK_MANAGEMENT_DASHBOARD_URL = `${PWA_BASE_URL}/app/dashboard/docas`;

interface SubmenuItem {
  icon?: React.ReactNode;
  title: string;
  info?: string;
  to?: string;
  onClick?: () => void;
  isAllowed: boolean;
}

interface MenuItem {
  icon?: React.ReactNode;
  title: string;
  info?: string;
  to?: string;
  onClick?: () => void;
  isAllowed: boolean;
  hasSubmenu?: boolean;
  submenu?: SubmenuItem[];
}

const LeftPanel: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userBelongsToAnyOf } = useAuth();
  const { data: recall } = useSelector((state: RootState) => state.recallSlice);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const handleCollapse = useCallback(
    () => dispatch(setRecall(!recall)),
    [dispatch, recall]
  );

  const openPwaPage = useCallback(async (url: string, pageName: string) => {
    const pwaWindow = window.open('', '_blank');

    if (!pwaWindow) {
      notify('error', `Não foi possível abrir ${pageName} em uma nova aba`);
      return;
    }

    try {
      const { data } = await api.post<{ code: string }>('auth/pwa/sso/code');
      pwaWindow.location.replace(`${url}#sso=${encodeURIComponent(data.code)}`);
    } catch {
      pwaWindow.close();
      notify('error', `Não foi possível abrir ${pageName} agora`);
    }
  }, []);

  const openPwaDashboard = useCallback(
    () => openPwaPage(PWA_DASHBOARD_URL, 'o Dashboard'),
    [openPwaPage]
  );
  const openPwaArrivals = useCallback(
    () => openPwaPage(PWA_ARRIVALS_URL, 'o Monitor de Chegada'),
    [openPwaPage]
  );
  const openPwaDockManagementDashboard = useCallback(
    () =>
      openPwaPage(
        PWA_DOCK_MANAGEMENT_DASHBOARD_URL,
        'o Dashboard de Gestão de Docas'
      ),
    [openPwaPage]
  );

  const handleSubmenuToggle = useCallback(
    (item: MenuItem) => {
      if (item.to) {
        navigate(item.to);
      }
      setOpenSubmenu(openSubmenu === item.title ? null : item.title);
    },
    [openSubmenu, navigate]
  );

  const menuItems = useMemo((): MenuItem[] => {
    return [
      {
        icon: <S.BarChartIcon />,
        title: 'Dashboard',
        info: 'Acompanhe os indicadores gerais do agendamento.',
        to: '',
        isAllowed: userBelongsToAnyOf('admin'),
        hasSubmenu: true,
        submenu: [
          {
            title: 'Expedição',
            to: '/dashboard/expedicao',
            isAllowed: userBelongsToAnyOf('admin'),
            icon: '•',
          },
        ],
      },
      {
        icon: <S.TruckIcon />,
        title: 'Jornada do veículo',
        info: 'Acompanhe as etapas da jornada dos veículos.',
        to: '',
        isAllowed: true,
        hasSubmenu: true,
        submenu: [
          {
            title: 'Dashboard',
            onClick: openPwaDashboard,
            isAllowed: true,
            icon: '•',
          },
          {
            title: 'Parametrização',
            to: '/jornada-veiculo/parametrizacoes',
            isAllowed: userBelongsToAnyOf('admin'),
            icon: '•',
          },
          {
            title: 'Controle de acesso',
            to: '/jornada-veiculo/controle-de-acesso',
            isAllowed: userBelongsToAnyOf('admin'),
            icon: '•',
          },
        ],
      },
      {
        icon: <S.WarehouseIcon />,
        title: 'Gestão de Docas',
        info: 'Acompanhe a ocupação e a disponibilidade das docas.',
        to: '',
        isAllowed: true,
        hasSubmenu: true,
        submenu: [
          {
            title: 'Dashboard',
            onClick: openPwaDockManagementDashboard,
            isAllowed: true,
            icon: '•',
          },
        ],
      },
      {
        icon: <S.BookMarkIcon />,
        title: 'Importação',
        info: 'Importe planilhas e gerencie a quarentena de dados externos.',
        to: '',
        isAllowed: userBelongsToAnyOf('admin', 'warehouseMember'),
        hasSubmenu: true,
        submenu: [
          {
            title: 'Importar Hora Certa',
            to: '/importacao/hora-certa',
            isAllowed: userBelongsToAnyOf('admin', 'warehouseMember'),
            icon: '•',
          },
          {
            title: 'Quarentena',
            to: '/importacao/quarentena',
            isAllowed: userBelongsToAnyOf('admin', 'warehouseMember'),
            icon: '•',
          },
        ],
      },
      {
        icon: <S.WarehouseIcon />,
        title: 'Portaria',
        info: 'Gerencie as entradas e saídas de veículos na portaria.',
        to: '/portaria',
        isAllowed: true,
      },
      {
        icon: <S.TruckIcon />,
        title: 'Monitor de Chegada',
        info: 'Acompanhe agendamentos e status das operações da portaria.',
        isAllowed: true,
        hasSubmenu: true,
        submenu: [
          {
            title: 'Dashboard',
            onClick: openPwaArrivals,
            isAllowed: true,
            icon: '•',
          },
          {
            title: 'Mapeamento de checkpoints',
            to: '/painel-chegadas/mapeamento-checkpoints',
            isAllowed: userBelongsToAnyOf('admin'),
            icon: '•',
          },
        ],
      },
      {
        icon: <S.ListIcon />,
        title: 'Painel Gerencial',
        info: 'Acompanhe/busque os agendamento em formato de lista.',
        to: '/',
        isAllowed: !userBelongsToAnyOf('checklistMember'),
      },
      {
        icon: <S.CalendarIcon />,
        title: 'Agendamento',
        info: 'Acompanhe os agendamento em formato de calendário e por doca.',
        to: '/agendamento',
        isAllowed: !userBelongsToAnyOf('checklistMember'),
      },
      {
        icon: <S.ListIcon />,
        title: 'Checklist',
        info: 'Acompanhe os checklists avulsos realizados no sistema.',
        to: '/checklist/avulsos-approved',
        isAllowed: userBelongsToAnyOf('admin', 'checklistMember'),
      },
      {
        icon: <S.UserPinIcon />,
        title: 'Meus dados',
        info: 'Altere seus dados de acesso ao sistema.',
        to: '/meus-dados',
        isAllowed: true,
      },
      {
        icon: <S.SettingsIcon />,
        title: 'Configurações',
        info: 'Acesso exclusivo para administradores do sistema.',
        to: '/configuracoes',
        isAllowed: userBelongsToAnyOf('admin'),
      },
      {
        icon: <S.ReservedIntervalIcon />,
        title: 'Bloqueio de Horários',
        info: 'Crie intervalos de bloqueio para docas',
        to: '/configuracoes/bloqueio-de-horarios',
        isAllowed: userBelongsToAnyOf('warehouseMember'),
      },
      {
        icon: <S.BookMarkIcon />,
        title: 'Documentações',
        info: 'Acesse manuais e instruções de uso do sistema.',
        to: '/documentacoes',
        isAllowed: true,
      },
    ];
  }, [
    openPwaArrivals,
    openPwaDashboard,
    openPwaDockManagementDashboard,
    userBelongsToAnyOf,
  ]);

  const isMenuActive = useCallback(
    (item: MenuItem | SubmenuItem): boolean => {
      if (!item.to) return false;
      return item.to.split('/')[1] === location.pathname.split('/')[1];
    },
    [location]
  );

  const renderMenuItem = useCallback(
    (item: MenuItem, index: number) => {
      if (item.hasSubmenu) {
        return (
          <div key={index}>
            <S.MenuButton
              onClick={() => handleSubmenuToggle(item)}
              $active={openSubmenu === item.title && isMenuActive(item)}
              $expanded={openSubmenu === item.title}
            >
              <S.MenuItemHeader>
                {item.icon} <p>{item.title}</p>
              </S.MenuItemHeader>
              {item?.info && <S.MenuItemBody>{item.info}</S.MenuItemBody>}
            </S.MenuButton>
            {openSubmenu === item.title && item.submenu && (
              <S.Submenu>
                {item.submenu
                  .filter(({ isAllowed }) => isAllowed)
                  .map((subItem, subIndex) => (
                    <S.SubmenuItem
                      key={subIndex}
                      $active={isMenuActive(subItem)}
                    >
                      <S.MenuItemHeader>
                        {subItem.icon && subItem.icon}
                        {subItem.to ? (
                          <S.SubmenuLink to={subItem.to}>
                            <p>{subItem.title}</p>
                          </S.SubmenuLink>
                        ) : subItem.onClick ? (
                          <S.SubmenuAction
                            type="button"
                            onClick={subItem.onClick}
                          >
                            <p>{subItem.title}</p>
                          </S.SubmenuAction>
                        ) : (
                          <p>{subItem.title}</p>
                        )}
                      </S.MenuItemHeader>
                      {subItem?.info && (
                        <S.MenuItemBody>{subItem.info}</S.MenuItemBody>
                      )}
                    </S.SubmenuItem>
                  ))}
              </S.Submenu>
            )}
          </div>
        );
      }

      const menuItemContent = (
        <>
          <S.MenuItemHeader>
            {item.icon} <p>{item.title}</p>
          </S.MenuItemHeader>
          {item?.info && <S.MenuItemBody>{item.info}</S.MenuItemBody>}
        </>
      );

      if (item.onClick) {
        return (
          <S.MenuItemAction
            type="button"
            aria-label={item.title}
            onClick={item.onClick}
            key={index}
            $active={isMenuActive(item)}
          >
            {menuItemContent}
          </S.MenuItemAction>
        );
      }

      return (
        <S.MenuItem to={item.to ?? ''} key={index} $active={isMenuActive(item)}>
          {menuItemContent}
        </S.MenuItem>
      );
    },
    [openSubmenu, handleSubmenuToggle, isMenuActive]
  );

  return (
    <S.Container>
      <S.CollapseButton onClick={handleCollapse} recall={recall}>
        <S.ArrowIcon />
      </S.CollapseButton>
      <S.LeftPanel recall={recall}>
        <S.Header>
          <S.Logo>
            <S.LogoImage src={require('assets/images/light-logo.png')} />
          </S.Logo>
        </S.Header>
        <S.NavigationMenu>
          {menuItems.filter(({ isAllowed }) => isAllowed).map(renderMenuItem)}
        </S.NavigationMenu>
        <S.Footer>
          {!recall && (
            <p>{new Date().getFullYear()} © Todos os direitos reservados</p>
          )}
        </S.Footer>
      </S.LeftPanel>
    </S.Container>
  );
};

export default memo(LeftPanel);
